import { degreeToRadian } from "common/utils/math";
import type { Vec2 } from "geom/vec2";
import { vec2Len, vec2Normalize, vec2Rotate } from "geom/vec2";
import { jsxToString } from "lib/house/annotation/conversion";
import type { BaseAnnoProps, BaseStyleProps } from "lib/house/annotation/svgSettings";
import { baseAnnoDefault, baseStyleDefault } from "lib/house/annotation/svgSettings";

interface ContLoadConnectionOnlyProps extends BaseAnnoProps {
  controllerPosition: Vec2;
  controllerNormal: Vec2;
  loadPosition: Vec2;
  normalAdjust?: number;
  flipGuideVectorRotation?: boolean;
}

export type ContLoadConnectionProps = ContLoadConnectionOnlyProps &
  Pick<
    BaseStyleProps,
    | "fillLightPrimary"
    | "strokeHalfTonePrimary"
    | "strokeDarkPrimary"
    | "extraSmallStrokeWidth"
    | "textFillPrimary"
    | "regularFontSize"
    | "fontFamily0"
    | "ultraSmallStrokeWidth"
  >;

export const contLoadConnectionDefault: ContLoadConnectionProps = {
  ...baseStyleDefault,
  ...baseAnnoDefault,
  controllerPosition: [0, 0],
  controllerNormal: [0, 1],
  loadPosition: [100, 100],
};

export function generateContLoadConnection(props?: ContLoadConnectionProps, wrapSvg = false): string {
  return jsxToString(<ContLoadConnection {...contLoadConnectionDefault} {...props} />, wrapSvg);
}

const ContLoadConnection = (props: ContLoadConnectionProps) => {
  const curveRotation = props.flipGuideVectorRotation ? -30 : 30;
  const normalAdjust = props.normalAdjust ?? 1;
  const [sX, sY] = props.controllerPosition;
  const [rawSnX, rawSnY] = props.controllerNormal;
  const [snX, snY] = vec2Normalize(rawSnX, -rawSnY);
  const [eX, eY] = props.loadPosition;

  const [seVecX, seVecY] = [
    sX + snX * 14 * normalAdjust + snX * 50 * normalAdjust - eX,
    sY + snY * 14 * normalAdjust + snY * 50 * normalAdjust - eY,
  ];
  const [senVecX, senVecY] = vec2Normalize(seVecX, seVecY);
  const dLength = vec2Len(seVecX, seVecY);
  let [ecX, ecY] = vec2Rotate((senVecX * dLength) / 3, (senVecY * dLength) / 3, -degreeToRadian(curveRotation));
  ecX = ecX + eX;
  ecY = ecY + eY;

  const scale = props.scale;

  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <path
        d={`M ${sX + snX * 14 * normalAdjust} ${sY + snY * 14 * normalAdjust} C ${
          sX + snX * 14 * normalAdjust + snX * 50 * normalAdjust
        } ${sY + snY * 14 * normalAdjust + snY * 50 * normalAdjust}, ${ecX} ${ecY}, ${eX} ${eY}`}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.ultraSmallStrokeWidth}
        fill="none"
        strokeDasharray="5"
      />
    </g>
  );
};

export default ContLoadConnection;
